import { createBusinessCenterOperationalStateStore } from "../../lib/businessCenterOperationalStateStore";

export type ARRecoveryClientStatus = "Prospect" | "Onboarding" | "Active" | "Inactive" | "Suspended" | "Archived" | "Terminated";
export type ARRecoveryClientContactMethod = "Email" | "Phone" | "Email + Phone";

export interface ARRecoveryClientContact {
  id: string;
  name: string;
  title: string;
  email: string;
  phone: string;
  preferredContactMethod: ARRecoveryClientContactMethod;
  notes: string;
}

export interface ARRecoveryClientTimelineEvent {
  id: string;
  type: "Client Created" | "Agreement Signed" | "Onboarding Completed" | "Portfolio Imported" | "Assigned Manager" | "Major Update" | "Note";
  detail: string;
  actorName: string;
  createdAt: string;
}

export interface ARRecoveryClientNote {
  id: string;
  body: string;
  internalOnly: true;
  actorName: string;
  actorEmail: string;
  createdAt: string;
}

export interface ARRecoveryClientRecord {
  id: string;
  companyId: string;
  companyName: string;
  dba: string;
  industry: string;
  website: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  primaryContactPreferredContactMethod: ARRecoveryClientContactMethod;
  accountsPayableContactName: string;
  accountsPayableContactEmail: string;
  accountsPayableContactPhone: string;
  controllerContactName: string;
  controllerContactEmail: string;
  controllerContactPhone: string;
  cfoContactName: string;
  cfoContactEmail: string;
  cfoContactPhone: string;
  status: ARRecoveryClientStatus;
  clientSince: string;
  servicePackage: string;
  billing: {
    contactName: string;
    email: string;
    phone: string;
    paymentTerms: string;
  };
  contacts: ARRecoveryClientContact[];
  assignments: {
    managerUserId: string;
    collectorUserIds: string[];
    backupCollectorUserId: string;
  };
  settings: {
    letterBranding: "Future AR Recovery" | "Client Brand";
    statementBranding: "Future AR Recovery" | "Client Brand";
    contactPreferences: string;
    escalationRules: string;
    notificationRules: string;
  };
  customerAccountIds: string[];
  timeline: ARRecoveryClientTimelineEvent[];
  notes: ARRecoveryClientNote[];
  internalNotes: string;
  communicationHistoryPlaceholder: string;
  agreementSignedAt: string | null;
  onboardingCompletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const CLIENT_COMPANY_ID = "company-future-ar-recovery";

const clientStore = createBusinessCenterOperationalStateStore<ARRecoveryClientRecord[]>({
  moduleKey: "ar_recovery_clients",
  scope: {
    scopeType: "company",
    companyId: CLIENT_COMPANY_ID,
  },
  seed: () => [],
  normalize: (clients) => (Array.isArray(clients) ? clients.map(normalizeClient) : []),
});

export function loadARRecoveryClients() {
  return clientStore.read();
}

export function refreshARRecoveryClients() {
  return clientStore.refresh();
}

export async function saveARRecoveryClients(clients: ARRecoveryClientRecord[]) {
  return clientStore.writeAsync(clients);
}

export async function upsertARRecoveryClient(client: ARRecoveryClientRecord) {
  const current = loadARRecoveryClients();
  const normalized = normalizeClient({ ...client, updatedAt: new Date().toISOString() });
  const duplicate = current.find(
    (item) =>
      item.id !== normalized.id &&
      item.companyName.trim().toLowerCase() === normalized.companyName.trim().toLowerCase(),
  );
  if (duplicate) throw new Error(`Client "${normalized.companyName}" already exists.`);
  const exists = current.some((item) => item.id === normalized.id);
  const next = exists
    ? current.map((item) => (item.id === normalized.id ? normalized : item))
    : [normalized, ...current];
  return saveARRecoveryClients(next);
}

export function createARRecoveryClientId() {
  return `ar-client-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createARRecoveryClientContactId() {
  return `ar-client-contact-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createARRecoveryClientEventId() {
  return `ar-client-event-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createARRecoveryClientNoteId() {
  return `ar-client-note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeClient(client: ARRecoveryClientRecord): ARRecoveryClientRecord {
  const now = new Date().toISOString();
  return {
    ...client,
    companyId: client.companyId || CLIENT_COMPANY_ID,
    companyName: client.companyName?.trim() || "Untitled Client",
    dba: client.dba?.trim() || "",
    industry: client.industry?.trim() || "",
    website: client.website?.trim() || "",
    primaryContactName: client.primaryContactName?.trim() || "",
    primaryContactEmail: client.primaryContactEmail?.trim() || "",
    primaryContactPhone: client.primaryContactPhone?.trim() || "",
    primaryContactPreferredContactMethod: client.primaryContactPreferredContactMethod || "Email",
    accountsPayableContactName: client.accountsPayableContactName?.trim() || "",
    accountsPayableContactEmail: client.accountsPayableContactEmail?.trim() || "",
    accountsPayableContactPhone: client.accountsPayableContactPhone?.trim() || "",
    controllerContactName: client.controllerContactName?.trim() || "",
    controllerContactEmail: client.controllerContactEmail?.trim() || "",
    controllerContactPhone: client.controllerContactPhone?.trim() || "",
    cfoContactName: client.cfoContactName?.trim() || "",
    cfoContactEmail: client.cfoContactEmail?.trim() || "",
    cfoContactPhone: client.cfoContactPhone?.trim() || "",
    status: normalizeClientStatus(client.status),
    clientSince: client.clientSince || "",
    servicePackage: client.servicePackage?.trim() || "",
    billing: {
      contactName: client.billing?.contactName?.trim() || "",
      email: client.billing?.email?.trim() || "",
      phone: client.billing?.phone?.trim() || "",
      paymentTerms: client.billing?.paymentTerms?.trim() || "",
    },
    contacts: Array.isArray(client.contacts) ? client.contacts : [],
    assignments: {
      managerUserId: client.assignments?.managerUserId || "",
      collectorUserIds: Array.isArray(client.assignments?.collectorUserIds) ? client.assignments.collectorUserIds : [],
      backupCollectorUserId: client.assignments?.backupCollectorUserId || "",
    },
    settings: {
      letterBranding: client.settings?.letterBranding === "Client Brand" ? "Client Brand" : "Future AR Recovery",
      statementBranding: client.settings?.statementBranding === "Client Brand" ? "Client Brand" : "Future AR Recovery",
      contactPreferences: client.settings?.contactPreferences || "",
      escalationRules: client.settings?.escalationRules || "",
      notificationRules: client.settings?.notificationRules || "",
    },
    customerAccountIds: Array.isArray(client.customerAccountIds) ? client.customerAccountIds : [],
    timeline: Array.isArray(client.timeline) ? client.timeline : [],
    notes: Array.isArray(client.notes) ? client.notes : [],
    internalNotes: client.internalNotes?.trim() || "",
    communicationHistoryPlaceholder: client.communicationHistoryPlaceholder?.trim() || "Communication history will appear here once outreach and service workflows are connected.",
    agreementSignedAt: client.agreementSignedAt || null,
    onboardingCompletedAt: client.onboardingCompletedAt || null,
    createdAt: client.createdAt || now,
    updatedAt: client.updatedAt || now,
  };
}

function normalizeClientStatus(status: string): ARRecoveryClientStatus {
  if (status === "Paused") return "Inactive";
  if (status === "Closed") return "Archived";
  return ["Prospect", "Onboarding", "Active", "Inactive", "Suspended", "Archived", "Terminated"].includes(status)
    ? (status as ARRecoveryClientStatus)
    : "Prospect";
}
